import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSWRConfig } from 'swr'
import useSWRInfinite from 'swr/infinite'
import { toast } from 'sonner'
import { FileText, FlaskConical, Loader2, Sparkles, Table2, Users } from '@digital-twins/design-system/icons'
import {
  LibraryCollectionDetail,
  LibraryCollectionItem,
  LibraryCollectionResourcesPage,
  LibraryItemKind,
  LibraryMemberMode,
  LibraryMembership,
  LibraryResourceKind,
  toLibraryMemberStoredKind,
} from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/Text'
import { libraryCollectionsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { SocialMeasureSwitch } from '@/pages/KnowledgeHubPage/social/SocialStatRow'
import { useCollectionResources } from '@/pages/KnowledgeCollectionPage/useCollectionResources'
import { useCollectionTopics } from '@/pages/KnowledgeCollectionPage/useCollectionTopics'
import { revalidateLibraryCollection } from '@/pages/KnowledgeCollectionPage/useLibraryCollection'
import { CollectionResourceFilters } from '@/pages/KnowledgeCollectionPage/useCollectionFilters'
import { PICKER_RESOURCE_KINDS, PICKER_SEARCH_DEBOUNCE_MS } from './collectionPickerConstants'

export interface CollectionAddPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  collection: LibraryCollectionDetail
  initialTab?: 'resources' | 'topics'
  /** A member was included or excluded: the page refetches its reads. */
  onChanged: () => void
}

type PickerTab = 'resources' | 'topics'

export { PICKER_RESOURCE_KINDS, PICKER_SEARCH_DEBOUNCE_MS } from './collectionPickerConstants'

export const LIBRARY_CANDIDATES_SWR_KEY = 'library/candidates'

const NO_FILTERS: CollectionResourceFilters = { market: [], year: [], brand: [] }

const KIND_ICONS = {
  document: FileText,
  dataset: Table2,
  audience: Users,
  study: FlaskConical,
  topic: Sparkles,
} as const

/** A picker row's state in this collection: a mode set here wins over what the reads say. */
type RowState = { mode: LibraryMemberMode } | { membership: LibraryMembership } | null

function memberKey(kind: LibraryItemKind, token: string): string {
  return `${toLibraryMemberStoredKind(kind)}:${token}`
}

type CandidatesKey = [string, LibraryItemKind, string, string | null]

/**
 * The candidates of one kind over `GET /library/candidates?kind=&q=&cursor=`,
 * a page at a time. The server applies D7, D8 and D15; nothing is filtered
 * here.
 */
function useCandidates(kind: LibraryItemKind, q: string, isEnabled: boolean) {
  const { data, error, isLoading, isValidating, size, setSize } = useSWRInfinite<LibraryCollectionResourcesPage>(
    (pageIndex: number, previous: LibraryCollectionResourcesPage | null): CandidatesKey | null => {
      if (!isEnabled) {
        return null
      }
      if (pageIndex > 0 && !previous?.nextCursor) {
        return null
      }
      return [LIBRARY_CANDIDATES_SWR_KEY, kind, q, previous?.nextCursor ?? null]
    },
    (key: CandidatesKey) =>
      libraryCollectionsApi.listCandidates({
        kind: key[1],
        ...(key[2] ? { q: key[2] } : {}),
        ...(key[3] ? { cursor: key[3] } : {}),
      }),
    { revalidateOnFocus: false, revalidateFirstPage: false, shouldRetryOnError: false },
  )

  const pages = data ?? []
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : null
  const hasMore = Boolean(lastPage?.nextCursor)
  const isLoadingMore = isValidating && size > pages.length

  return {
    items: pages.flatMap((page) => page.items),
    isLoading: isEnabled && isLoading,
    isLoadingMore,
    hasError: Boolean(error),
    hasMore,
    loadMore: () => {
      if (hasMore && !isLoadingMore) {
        void setSize(size + 1)
      }
    },
  }
}

/**
 * "Add" on a collection page: Resources and Topics tabs with a search, fed
 * only by `GET /library/candidates` (the server keeps out what the viewer may
 * not add, D7, D8, D15). Each row can be included or excluded; rows already
 * in the collection show their state from the collection's own reads, and a
 * rule member keeps the "Linked by rule" hint.
 */
export function CollectionAddPicker(props: CollectionAddPickerProps) {
  const { open } = props

  if (!open) {
    return null
  }

  return <CollectionAddPickerSession key={props.collection.token} {...props} />
}

function CollectionAddPickerSession(props: CollectionAddPickerProps) {
  const { onOpenChange, collection, initialTab = 'resources', onChanged } = props

  const { t } = useTranslation('app')
  const [tab, setTab] = useState<PickerTab>(initialTab)
  const [resourceKind, setResourceKind] = useState<LibraryResourceKind>('document')
  const [query, setQuery] = useState('')
  const [q, setQ] = useState('')
  const [modes, setModes] = useState<Map<string, LibraryMemberMode>>(() => new Map())
  const [pendingKey, setPendingKey] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setQ(query.trim()), PICKER_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const { mutate: mutateScoped } = useSWRConfig()
  const topics = useCollectionTopics(collection.token)
  const showsTopics = topics.socialListeningEnabled
  const activeTab: PickerTab = tab === 'topics' && showsTopics ? 'topics' : 'resources'
  const kind: LibraryItemKind = activeTab === 'topics' ? 'topic' : resourceKind
  const candidates = useCandidates(kind, q, true)
  const members = useCollectionResources(activeTab === 'resources' ? collection.token : null, resourceKind, NO_FILTERS)

  const memberships = useMemo(() => {
    const byKey = new Map<string, LibraryMembership>()
    for (const item of members.items) {
      byKey.set(memberKey(item.kind, item.token), item.membership)
    }
    for (const topic of topics.topics) {
      byKey.set(memberKey('topic', topic.token), topic.membership)
    }
    return byKey
  }, [members.items, topics.topics])

  const rowStateOf = (item: LibraryCollectionItem): RowState => {
    const key = memberKey(item.kind, item.token)
    const mode = modes.get(key)
    if (mode) {
      return { mode }
    }
    const membership = memberships.get(key)
    return membership ? { membership } : null
  }

  const onSetMode = async (item: LibraryCollectionItem, mode: LibraryMemberMode) => {
    const key = memberKey(item.kind, item.token)
    setPendingKey(key)
    try {
      await libraryCollectionsApi.addMember(collection.token, { kind: item.kind, token: item.token, mode })
      setModes((current) => new Map(current).set(key, mode))
      toast.success(
        t(mode === 'include' ? 'library.collections.menu.added' : 'library.collections.menu.removed', {
          name: collection.name,
        }),
      )
      // The paged sections hold their filters in the key, so they refetch through `refresh`.
      await Promise.all([
        revalidateLibraryCollection(mutateScoped, collection.token, [{ kind: item.kind, token: item.token }]),
        members.refresh(),
      ])
      onChanged()
    } catch {
      toast.error(t('library.collections.menu.updateFailed'))
    } finally {
      setPendingKey(null)
    }
  }

  const tabOptions = [
    { value: 'resources' as const, label: t('library.collections.picker.tabs.resources') },
    ...(showsTopics ? [{ value: 'topics' as const, label: t('library.collections.picker.tabs.topics') }] : []),
  ]
  const kindOptions = PICKER_RESOURCE_KINDS.map((value) => ({
    value,
    label: t(`library.collections.resources.kinds.${value}`),
  }))

  const emptyLabel = q
    ? t('library.collections.picker.noResults')
    : t(activeTab === 'topics' ? 'library.collections.picker.noTopics' : 'library.collections.picker.noResources')

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[640px] flex-col gap-0 overflow-hidden rounded-[calc(var(--radius)+8px)] border-card-border bg-background p-0 sm:h-[min(680px,calc(100dvh-2rem))] sm:max-w-[640px] sm:p-0"
        data-testid="collection-add-picker"
      >
        <div className="flex shrink-0 flex-col gap-4 border-b border-card-border px-5 pb-4 pt-6 sm:px-8 sm:pt-8">
          <DialogTitle className="pr-8 font-display text-2xl font-normal leading-tight">
            {t('library.collections.picker.title')}
          </DialogTitle>
          <div className="flex flex-wrap items-center gap-2">
            {tabOptions.length > 1 ? (
              <SocialMeasureSwitch
                options={tabOptions}
                value={activeTab}
                onChange={setTab}
                ariaLabel={t('library.collections.picker.title')}
                testIdPrefix="collection-add-picker-tab"
              />
            ) : null}
            {activeTab === 'resources' ? (
              <SocialMeasureSwitch
                options={kindOptions}
                value={resourceKind}
                onChange={setResourceKind}
                ariaLabel={t('library.collections.resources.kindRailAria')}
                testIdPrefix="collection-add-picker-kind"
              />
            ) : null}
          </div>
          <Input
            type="search"
            value={query}
            placeholder={t('library.collections.picker.search')}
            aria-label={t('library.collections.picker.search')}
            className="rounded-full"
            autoComplete="off"
            data-testid="collection-add-picker-search"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-5 py-4 sm:px-8"
          aria-busy={candidates.isLoading}
          data-testid="collection-add-picker-list"
        >
          {candidates.isLoading ? (
            <div className="flex flex-col gap-2 animate-in fade-in fill-mode-both delay-150" aria-hidden="true">
              {[0, 1, 2].map((index) => (
                <span key={index} className="h-14 rounded-xl border border-card-border" />
              ))}
            </div>
          ) : candidates.items.length === 0 ? (
            <Text size="sm" color="muted" className="py-6 text-center" data-testid="collection-add-picker-empty">
              {emptyLabel}
            </Text>
          ) : (
            <ul className="flex flex-col divide-y divide-card-border">
              {candidates.items.map((item, index) => (
                <PickerRow
                  key={`${item.kind}:${item.token}`}
                  item={item}
                  index={index}
                  state={rowStateOf(item)}
                  isPending={pendingKey === memberKey(item.kind, item.token)}
                  isDisabled={pendingKey !== null}
                  onSetMode={(mode) => void onSetMode(item, mode)}
                />
              ))}
            </ul>
          )}
          {candidates.hasMore ? (
            <div className="flex justify-center pt-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={candidates.isLoadingMore}
                data-testid="collection-add-picker-more"
                onClick={candidates.loadMore}
              >
                {candidates.isLoadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                {t('library.collections.showMore')}
              </Button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface PickerRowProps {
  item: LibraryCollectionItem
  index: number
  state: RowState
  isPending: boolean
  isDisabled: boolean
  onSetMode: (mode: LibraryMemberMode) => void
}

function PickerRow(props: PickerRowProps) {
  const { item, index, state, isPending, isDisabled, onSetMode } = props

  const { t } = useTranslation('app')

  const Icon = KIND_ICONS[item.kind]
  const testId = `collection-add-picker-row-${index}`
  const isIncluded = state !== null && ('mode' in state ? state.mode === 'include' : state.membership === 'manual')
  const isExcluded = state !== null && 'mode' in state && state.mode === 'exclude'
  const isRuleMember = state !== null && 'membership' in state && state.membership === 'rule'

  const pillClass = (isActive: boolean) =>
    cn(
      'h-7 rounded-full border px-3 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
      isActive ? 'border-primary/45 bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
    )

  return (
    <li className="flex min-w-0 items-center gap-3 py-3" data-testid={testId} data-item-token={item.token}>
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/10 text-primary/70"
        aria-hidden="true"
      >
        {item.imageUrl ? (
          <img src={item.imageUrl} alt="" decoding="async" className="h-full w-full object-cover" />
        ) : (
          <Icon className="h-4 w-4" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm text-foreground" data-testid={`${testId}-title`}>
          {item.title}
        </span>
        {isRuleMember ? (
          <span className="text-xs text-muted-foreground" data-testid={`${testId}-rule`}>
            {t('library.collections.picker.linkedByRule')}
          </span>
        ) : item.subtitle ? (
          <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>
        ) : null}
      </span>
      {isPending ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" /> : null}
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          aria-pressed={isIncluded}
          className={pillClass(isIncluded)}
          disabled={isDisabled || isIncluded}
          data-testid={`${testId}-include`}
          onClick={() => onSetMode('include')}
        >
          {t('library.collections.picker.include')}
        </button>
        <button
          type="button"
          aria-pressed={isExcluded}
          className={pillClass(isExcluded)}
          disabled={isDisabled || isExcluded}
          data-testid={`${testId}-exclude`}
          onClick={() => onSetMode('exclude')}
        >
          {t('library.collections.picker.exclude')}
        </button>
      </div>
    </li>
  )
}
